import React, {useState, useEffect, useRef} from 'react';
import {
    getBeneficiaryPPAList,
    getExportBeneficiaryPPAList,
    updateSettlementRecords,
    getBeneficiaryPPATrail,
} from '../../helper/Api';
import { useLocation } from 'react-router-dom';
import PpaFilter from '../Filter/PpaFilter';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Spinner from '../../components/Tools/spinner';
import CustomBreadcrumbs from '../../components/Tools/Breadcumbs';
import moment from "moment";
import { AiOutlineEye, AiOutlineVerticalAlignBottom } from 'react-icons/ai';
import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Button,
    IconButton,
    Modal,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
} from '@mui/material';



export default function BeneficiaryPpa() {
    const {authData} = useSelector((state) => state.authData);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [searchStr, setToSearchStr] = useState('');
    const [data, setData] = useState([]);
    const [dataLength, setDataLength] = useState(0);
    const location = useLocation();
    const [departmentId, setDepartmentId] = useState('');
    const [filter, setFilter] = useState('own');
    const [tier, setTier] = useState('');
    const [exportExcel, setExport] = useState('');
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [openModal, setOpenModal] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState([]);
    const departmentRef = useRef({
        departmentId1: '',
        departmentId: '',
    });
    const pageRef = useRef({
        start: 0,
        end: 0,
        currentPage: 1,
    });

    const handleDateChange = (start, end, searchStr, fyId, tier, department_id, departmentId1, exportToExcel) => {
        setFromDate(start);
        setToDate(end);
        setToSearchStr(searchStr);
        setDepartmentId(department_id);
        setTier(tier);
        departmentRef.current.departmentId1 = departmentId1;
        setExport(exportToExcel);
    };

    // Same column layout as ConsolidateBeneficiaryPpa.jsx (single-department data is mapped in renderCellValue)
    const columns = [
        { Header: '#', accessor: 'sno', style: { width: '70px' } },
        { Header: 'Actions', accessor: 'ppa_url', style: { width: '100px' } },
        { Header: 'Status', accessor: 'status', style: { width: '130px' } },
        { Header: 'Department (Tier / Code)', accessor: 'department_info', style: { width: '250px' } },
        { Header: 'Scheme Info', accessor: 'scheme_info', style: { width: '250px' } },
        { Header: 'PPA Info', accessor: 'ppa_info', style: { width: '200px' } },
        { Header: 'voucher / order No', accessor: 'invoice_id', style: { width: '200px' } },
        { Header: 'Beneficiary Info', accessor: 'beneficiary_info', style: { width: '300px' } },
        { Header: 'Amount Info', accessor: 'amount_info', style: { width: '300px' } },
    ];

    const ALLOWED_HOSTS = [
            "tangy-media-sms.s3.ap-south-1.amazonaws.com"
        ];
    
    const downloadFile = (url) => {
        try {
            if (!url) {
            throw new Error("Empty URL");
            }

            const parsedUrl = new URL(url);

            // allow only https
            if (parsedUrl.protocol !== "https:") {
            throw new Error("Invalid protocol");
            }

            // allow only trusted domains
            if (!ALLOWED_HOSTS.includes(parsedUrl.hostname)) {
            throw new Error("Untrusted download source");
            }

            const link = document.createElement("a");
            link.href = parsedUrl.href;
            link.download = "PPA-Subactivity-Expense.xlsx";
            link.rel = "noopener noreferrer";
            link.target = "_blank";

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        } catch (err) {
            console.error("Blocked unsafe download URL:", err);
            toast.error("Invalid or unsafe download URL", {
            position: toast.POSITION.TOP_CENTER
            });
        }
    };

    const fetchExcelRecords = async () => {
        try {
            setLoading(true);
            const resp = await getExportBeneficiaryPPAList({
                from_date: fromDate, 
                to_date: toDate, 
                search_str: searchStr, 
                department_id: authData.user.department_id
            });
            if (resp.data.status) {
                setLoading(false);
                downloadFile(resp.data.url);
            }
        } catch (error) {
            setLoading(false);
            setData([]);
        }
    }

    const handlePageChange = async (newPage, limit, filterType) => {
        if(!filterType) {
            filterType = filter;
        }
        await updateSettlementRecords();
        if(exportExcel) {
            if (filterType == 'own') {
                await fetchExcelRecords();
            }
        }else {
            if (filterType == 'own') {
                await fetchRecords(limit, newPage);
            }
        }
    };

    const fetchRecords = async (limit, offset, department_id=null) => {
        try {
            setLoading(true);
            const resp = await getBeneficiaryPPAList({
                limit, offset, 
                from_date: fromDate, 
                to_date: toDate, search_str: searchStr, department_id
            });
            if (resp.data.status) {
                setData(resp.data.result.data);
                setDataLength(resp.data.result.total_count);
                pageRef.current.currentPage = offset;
                pageRef.current.start = (offset - 1) * limit;
                if(resp.data.result.data.length) {
                    pageRef.current.start += 1;
                }
                pageRef.current.end = (offset * limit) > resp.data.result.total_count ? resp.data.result.total_count : (offset * limit);
            }
            setLoading(false);
        } catch (error) {
            setData([]);
            setLoading(false);
            toast.error(error?.message || 'Failed to fetch records', {
                position: toast.POSITION.TOP_CENTER,
            });
        }
    }

    const fetchPaymentTrail = async (ppa_invoice_batch_id, account_no) => {
        try {
            setLoading(true);
            const response = await getBeneficiaryPPATrail({ ppa_invoice_batch_id, account_no });
            if (response && response.data) {
                setSelectedRowData(response.data.result || []);
            }
        } catch (error) {
            toast.error(error?.message || 'Failed to fetch payment trail', {
                position: toast.POSITION.TOP_CENTER,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = async (rowData) => {
        await fetchPaymentTrail(rowData.ppa_invoice_batch_id, rowData.reimbursement_beneficiary_account_no || rowData.account_no);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedRowData([]);
    };

    const getPaymentStatus = (payment_mode, flag_success_return) => {
        if (payment_mode === 'online') {
            if (flag_success_return === '1') return { label: 'Success', color: '#d4edda', textColor: '#155724' };
            if (flag_success_return === '0') return { label: 'Rejected', color: '#f8d7da', textColor: '#721c24' };
            if (flag_success_return === null) return { label: 'Pending', color: '#fff3cd', textColor: '#856404' };
            if (flag_success_return === '2') return { label: 'Failed', color: '#f8d7da', textColor: '#721c24' };
            if (flag_success_return === '3') return { label: 'On Hold', color: '#f8d7da', textColor: '#856404' };
            return { label: 'Sent To Bank', color: '#fff3cd', textColor: '#856404' };
        }
        return { label: 'Success', color: '#d4edda', textColor: '#155724' };
    };

    const downloadPPAFun = (url, ppa_id) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = `ppa_${ppa_id || 'download'}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const totalColumnWidth = columns.reduce((total, column) => {
        const columnWidth = parseInt(column?.style?.width, 10) || 0;
        return total + columnWidth;
    }, 0);

    const renderCellValue = (row, column, rowIndex) => {
        if (column.accessor === 'sno') {
            return page * rowsPerPage + rowIndex + 1;
        }

        if (column.accessor === 'ppa_url') {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Tooltip title="Download PPA" arrow>
                        <IconButton
                            size="small"
                            onClick={() => downloadPPAFun(row.ppa_url, row.id || row.ppa_no)}
                            sx={{
                                width: 28,
                                height: 28,
                                border: '1px solid #1976d2',
                                color: '#1976d2',
                                borderRadius: '6px',
                            }}
                        >
                            <AiOutlineVerticalAlignBottom size={16} />
                        </IconButton>
                    </Tooltip>
                    {String(row.payment_mode || '').toLowerCase() === 'online' && (
                        <Tooltip title="View PPA Payment Trail" arrow>
                            <IconButton
                                size="small"
                                onClick={() => handleOpenModal(row)}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    border: '1px solid #f59e0b',
                                    color: '#f59e0b',
                                    borderRadius: '6px',
                                }}
                            >
                                <AiOutlineEye size={16} />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
            );
        }

        if (column.accessor === 'status') {
            const statusMeta = getPaymentStatus(row.payment_mode, row.flag_success_return);
            return statusMeta ? (
                <span
                    style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor: statusMeta.color,
                        color: statusMeta.textColor,
                    }}
                >
                    {statusMeta.label}
                </span>
            ) : (
                'N/A'
            );
        }

        if (column.accessor === 'department_info') {
            return (
                <div style={{ lineHeight: 1.2 }}>
                    <div style={{ color: '#6b7280', marginTop: '4px' }}>{row?.name || '-'}</div>
                    <div style={{ fontWeight: 600 }}>Level: {row?.department_level || '-'}</div>
                </div>
            );
        }

        if (column.accessor === 'scheme_info') {
            return (
                <div style={{ lineHeight: 1.2 }}>
                    <div style={{ color: '#6b7280', marginTop: '4px' }}><b>Scheme:</b> {row?.scheme_name + ' - ' + row?.scheme_code || '-'}</div>

                    <div style={{ color: '#6b7280', marginTop: '4px' }}><b>Sub Head:</b> {row?.sub_head_name + ' - ' + row?.sub_head_code || '-'}</div>
                </div>
            );
        }

        if (column.accessor === 'ppa_info') {
            const paymentMode = String(row?.payment_mode || '').toLowerCase();
            const modeChip =
                paymentMode === 'online'
                    ? { label: 'Online', bg: '#e8f0ff', color: '#1f4db8' }
                    : { label: 'Offline', bg: '#e6f4ea', color: '#1e7e34' };

            return (
                <div style={{ lineHeight: 1.25 }}>
                    <div style={{ fontWeight: 600 }}>PPA: {row?.ppa_no || '-'}</div>
                    <div style={{ marginTop: '4px' }}>
                        <span
                            style={{
                                padding: '3px 8px',
                                borderRadius: '999px',
                                backgroundColor: modeChip.bg,
                                color: modeChip.color,
                                fontSize: '12px',
                                fontWeight: 600,
                                display: 'inline-block',
                            }}
                        >
                            {modeChip.label}
                        </span>
                    </div>
                    <div style={{ color: '#6b7280', marginTop: '2px' }}>
                        {row?.l3_approval_datetime
                            ? moment(row.l3_approval_datetime).format('DD-MM-YYYY hh:mm A')
                            : '-'}
                    </div>
                </div>
            );
        }

        if (column.accessor === 'invoice_id') {
            return row?.invoice_id || row?.voucher_order_no || '-';
        }

        if (column.accessor === 'beneficiary_info') {
            const name = row?.invoice_beneficiary_name || row?.beneficiary_name || '-';
            const account = row?.invoice_beneficiary_account_no || row?.account_no || '-';
            const ifsc = row?.invoice_beneficiary_ifsc_code || row?.ifsc_code || '-';
            return (
                <div style={{ lineHeight: 1.25 }}>
                    <div style={{ fontWeight: 600 }}>{name}</div>
                    <div style={{ color: '#6b7280', marginTop: '4px' }}>A/C: {account}</div>
                    <div style={{ color: '#6b7280', marginTop: '2px' }}>IFSC: {ifsc}</div>
                </div>
            );
        }

        if (column.accessor === 'amount_info') {
            const sanctioned = row?.invoice_sanctioned_amount ?? row?.sanctioned_amount ?? row?.reimbursement_gross_amount;
            const deduction = row?.invoice_total_deduction ?? row?.deduction_amount ?? row?.reimbursement_deduction_amount;
            const payment = row?.invoice_total_payable ?? row?.payment_amount ?? row?.reimbursement_net_amount;
            return (
                <div style={{ lineHeight: 1.25 }}>
                    <div style={{ fontWeight: 600 }}>Sanctioned: {sanctioned ?? '-'}</div>
                    <div style={{ color: '#6b7280', marginTop: '4px' }}>Deduction: {deduction ?? '-'}</div>
                    <div style={{ color: '#6b7280', marginTop: '2px' }}>Payment: {payment ?? '-'}</div>
                </div>
            );
        }

        return row?.[column.accessor] ?? '-';
    };
    
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const newReportType = queryParams.get('reportType') || 'own';
        setFilter(newReportType);
        setPage(0);
        setRowsPerPage((prev) => prev || 50);
        handlePageChange(1, rowsPerPage, newReportType);
    }, [fromDate, toDate, searchStr, departmentId, location.search, tier, exportExcel]);

    useEffect(() => {
        handlePageChange(page + 1, rowsPerPage, filter);
    }, [page, rowsPerPage]);
    

    return (
        <div>
            {loading && <Spinner />}
            <div className="row" style={{ padding: "10px 16px" }}>
                <div className="col-md-6">
                    <h4 style={{ color: "#28326f", margin:0}}>Beneficiary PPA Report</h4>
                    <CustomBreadcrumbs linksArray={[]} title="Beneficiary PPA Report"/>
                </div>
                <div className="col-md-6">
                    <div className="d-flex gap-2" style={{ width: "auto", float: "right" }}>
                        <Button variant="contained" className="navigation_button" onClick={() => fetchExcelRecords()}> Export Excel</Button>
                    </div>
                </div>
            </div>
            <PpaFilter onDateChange={handleDateChange} filterType={filter}  />
            <div className='scheme p-2'>
                <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                    <TableContainer sx={{ maxHeight: '72vh', overflowX: 'auto', overflowY: 'auto' }}>
                        <Table
                            stickyHeader
                            aria-label="sticky table"
                            sx={{
                                tableLayout: 'fixed',
                                width: `${totalColumnWidth}px`,
                                minWidth: `${totalColumnWidth}px`,
                            }}
                        >
                            <colgroup>
                                {columns.map((column) => (
                                    <col key={`col_${column.accessor}_${column?.style?.width || 'auto'}`} style={{ width: column?.style?.width }} />
                                ))}
                            </colgroup>
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell
                                            key={`head_${column.accessor}_${column?.style?.width || 'auto'}`}
                                            align={column.align}
                                            style={{
                                                minWidth: column?.style?.width || column.minWidth,
                                                width: column?.style?.width,
                                                maxWidth: column?.style?.width,
                                                whiteSpace: column.accessor === 'scheme_info' ? 'normal' : 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                ...(column.style || {}),
                                            }}
                                        >
                                            {column.label || column.Header}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.map((row, rowIndex) => (
                                    <TableRow
                                        hover
                                        role="checkbox"
                                        tabIndex={-1}
                                        key={`row_${row.id || 'noid'}_${row.ppa_no || 'noppa'}_${rowIndex}`}
                                    >
                                        {columns.map((column) => (
                                            <TableCell
                                                key={`cell_${column.accessor}_${row.id || 'noid'}_${row.ppa_no || 'noppa'}_${rowIndex}`}
                                                align={column.align}
                                                style={{
                                                    width: column?.style?.width,
                                                    maxWidth: column?.style?.width,
                                                    whiteSpace: column.accessor === 'scheme_info' ? 'normal' : 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: column.accessor === 'scheme_info' ? 'clip' : 'ellipsis',
                                                    wordBreak: column.accessor === 'scheme_info' ? 'break-word' : 'normal',
                                                }}
                                            >
                                                {renderCellValue(row, column, rowIndex)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[50, 100, 200, 500]}
                        component="div"
                        count={dataLength || data.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(event) => {
                            setRowsPerPage(+event.target.value);
                            setPage(0);
                        }}
                    />
                </Paper>
            </div>

            {/* Modal for Payment Trail */}
            <Modal
                open={openModal}
                onClose={handleCloseModal}
                aria-labelledby="payment-trail-modal"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        boxShadow: 24,
                        padding: '24px',
                        maxWidth: '900px',
                        width: '90%',
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h2 id="payment-trail-modal">Payment Trail Details</h2>
                        <Button onClick={handleCloseModal} sx={{ minWidth: 'auto', padding: '0' }}>
                            <CloseIcon />
                        </Button>
                    </Box>

                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #ddd', background: '#000' }}>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#fff' }}>Transaction ID</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#fff' }}>Date</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#fff' }}>Amount</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#fff' }}>Status</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#fff' }}>Remarks</th>
                            </tr>
                        </thead>
                        <tbody>
                            {selectedRowData.length > 0 ? (
                                selectedRowData.map((trail, index) => (
                                    <tr key={`trail_${trail.transaction_reference || 'notxn'}_${trail.created_at || 'nodate'}_${index}`} style={{ borderBottom: '1px solid #ddd' }}>
                                        <td style={{ padding: '12px' }}>{trail.transaction_reference || 'N/A'}</td>
                                        <td style={{ padding: '12px' }}>{trail.created_at || 'N/A'}</td>
                                        <td style={{ padding: '12px' }}>{trail.amount_paise || 'N/A'}</td>
                                        <td style={{ padding: '12px' }}>
                                            {getPaymentStatus('online', trail.flag_success_return) ? (
                                                <span
                                                    style={{
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        backgroundColor: getPaymentStatus('online', trail.flag_success_return).color,
                                                        color: getPaymentStatus('online', trail.flag_success_return).textColor
                                                    }}
                                                >
                                                    {getPaymentStatus('online', trail.flag_success_return).label}
                                                </span>
                                            ) : (
                                                'N/A'
                                            )}
                                        </td>
                                        <td style={{ padding: '12px' }}>{trail.remarks || 'N/A'}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} style={{ padding: '12px', textAlign: 'center' }}>No trail records found.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </Box>
            </Modal>
        </div>
    )
}